import React, {
  useEffect,
  useState,
  useCallback,
  useLayoutEffect,
  useContext,
  useRef,
} from 'react';
import {View, ScrollView} from 'react-native';
import {Divider} from 'react-native-paper';
import _ from 'lodash';
import moment from 'moment';
import {useIsFocused, useNavigation} from '@react-navigation/native';
import {HeaderBackButton} from '@react-navigation/elements';
import LogRocket from 'logrocket';

import sy from '~/styles';
import {
  Text,
  ServiceRequestStatusIcon,
  DealerPersona,
  LessorPersona,
  FormNumberInput,
  TabBar,
} from '~/components/controls';
import {Grouping} from '~/components/controls/3';
import {
  Row as GroupingRow,
  EditDialog as GroupingRowEdit,
} from '~/components/controls/3/Grouping';
import {Activity, Message} from '~/components/shared';

import Format from '~/lib/format';
import {
  service_request as serviceRequestApi,
  messages as messagesApi,
} from '~/api';
import withProtectedScreen from '~/components/hoc/with-protected-screen';
import {
  ROB_REPLACEMENT_CODE,
  REPLACEMENT_REASON_TITLE,
  ROB_REPLACEMENT_RENTAL_CHARGE_CODE,
  ROB_REPLACEMENT_KM_SURCHARGE_CODE,
  ROB_REPLACEMENT_FREE_KM_CODE,
  ROB_REPLACEMENT_STAGE,
  ROB_REPLACEMENT_STAGE_TITLE,
} from '~/types/replacement';
import {STATUS_DESCRIPTIONS} from '~/types/statuses';
import {isLessorStatus, isRobTaskCancelled} from '~/types/rob';
import {Tabs} from '~/types/tabs';
import {useThrottledCallback} from '~/lib/hooks';

import ChevronLeftIcon from '~/images/md-icons/chevron_left/materialicons/24px.svg';
import EventIcon from '~/images/md-icons/event/materialicons/24px.svg';
import CarRentalIcon from '~/images/md-icons/car_rental/materialicons/24px.svg';
import CarTagIcon from '~/images/md-icons/car_tag/materialsymbolsfilled/24px.svg';
import AssignmentIcon from '~/images/md-icons/assignment/materialicons/24px.svg';
import DateRangeIcon from '~/images/md-icons/date_range/materialicons/24px.svg';
import AddRoadIcon from '~/images/md-icons/add_road/materialicons/24px.svg';
import SyncAltIcon from '~/images/md-icons/sync_alt/materialicons/24px.svg';
import LocalGasStationIcon from '~/images/md-icons/local_gas_station/materialicons/24px.svg';
import GppBadIcon from '~/images/md-icons/gpp_bad/materialicons/24px.svg';
import DescriptionIcon from '~/images/md-icons/description/materialicons/24px.svg';
import BusinessIcon from '~/images/md-icons/business/materialicons/24px.svg';
import ChatIcon from '~/images/md-icons/chat/materialicons/24px.svg';
import AssignmentOutlinedIcon from '~/images/md-icons/assignment/materialsymbolsoutlined/24px.svg';
import ChatOutlinedIcon from '~/images/md-icons/chat/materialsymbolsoutlined/24px.svg';

const LessorRequestContext = React.createContext({});

const withLessorRequestContext = (Component) => (props) => {
  const {rob_request_id} = props.route.params ?? {};

  const navigation = useNavigation();

  const [loading, setLoading] = useState(true);
  const [service_request, setServiceRequest] = useState(null);
  const [replacement_vehicle, setReplacementVehicle] = useState(null);
  const [sla, setSla] = useState(null);
  const [is_cancelled, setIsCancelled] = useState(false);

  const refresh = useThrottledCallback(async () => {
    const serviceRequest = await serviceRequestApi.retrieve(rob_request_id);

    if (!serviceRequest) {
      navigation.replace('Tabs', {
        screen: 'Search',
      });
      return false;
    }

    setSla(
      serviceRequest.sla.replacement_vehicle?.reduce(
        (acc, item) => ({
          ...acc,
          [item.rob_code]: item,
        }),
        {},
      ),
    );

    const component = serviceRequest.components.find(
      (item) => item.rob_component.rob.code === ROB_REPLACEMENT_CODE,
    );

    setServiceRequest({
      lessor: serviceRequest.lessor,
      dealer: serviceRequest.dealer,
      car: serviceRequest.car,
      license: serviceRequest.license,
      status: serviceRequest.status,
      last_message: serviceRequest.last_message,
      fleet_contract_id: serviceRequest.fleet_contract_id,
      rob_contract: serviceRequest.rob_contract,
      component,
    });
    setReplacementVehicle(serviceRequest.replacement);

    const cancelled = isRobTaskCancelled(component.rob_component.status.code);
    setIsCancelled(cancelled);

    setLoading(false);
  }, [rob_request_id]);

  useEffect(() => {
    refresh();
  }, [rob_request_id]);

  useEffect(() => {
    if (service_request) {
      LogRocket.track(
        'Lessor Request Details',
        _.pickBy({
          rob_request_id,
          license: service_request.license,
          status: service_request.status,
        }),
      );
    }
  }, [rob_request_id, service_request]);

  return (
    <LessorRequestContext.Provider
      value={{
        rob_request_id,
        loading,
        service_request,
        replacement_vehicle,
        sla,
        is_cancelled,

        refresh,
      }}>
      <Component {...props} />
    </LessorRequestContext.Provider>
  );
};

const FuelText = ({style, fuel}) => {
  const fuelType = {
    gasoline: 'Benzine',
    diesel: 'Diesel',
    electric: 'Elektrisch',
    hybrid: 'Hybride',
    lng: 'LNG',
    lpg: 'LPG',
    other: 'Overig',
  }[fuel];

  return <Text style={style}>{fuelType}</Text>;
};

const RentalClassText = ({
  style,
  rental_class,
  rental_classes,
  contract_rental_class = null,
  rental_car_class = null,
}) => (
  <>
    {rental_class ? (
      <Text style={style}>
        {rental_class}-
        {
          rental_classes?.find((item) => item.code === rental_class)
            ?.description
        }
        {rental_class === contract_rental_class &&
        rental_class === rental_car_class ? (
          <>&nbsp;(contract&nbsp;&amp;&nbsp;inzet)</>
        ) : rental_class === contract_rental_class ? (
          <>&nbsp;(contract)</>
        ) : rental_class === rental_car_class ? (
          <>&nbsp;(inzet)</>
        ) : (
          <></>
        )}
      </Text>
    ) : (
      <></>
    )}
  </>
);

const LineItem = ({
  icon,
  title,
  price,
  subtitle,
  topMost,
  allowed = true,
  cancelled = false,
}) => (
  <>
    {!topMost && (
      <View style={[sy['px-4']]}>
        <Divider style={[sy['bg-gray95']]} />
      </View>
    )}
    <View
      style={[
        sy['flex-row'],
        sy['p-4'],
        sy['gap-4'],
        sy['items-center'],
        !allowed && sy['view-disabled'],
      ]}>
      {icon}
      <View style={[sy['flex-1']]}>
        <View style={[sy['flex-row'], sy['justify-between']]}>
          <Text>{title}</Text>
          <Text
            style={[sy.smallRegular, cancelled ? sy['line-through'] : null]}>
            {allowed && _.isFinite(price) ? Format.price(price) : <></>}
          </Text>
        </View>
        <Text style={[sy.smallRegular, sy['text-lightgray']]}>
          {allowed ? subtitle : <>Niet toegestaan</>}
        </Text>
      </View>
    </View>
  </>
);

const MessagesTab = ({focused}) => {
  const [messages, setMessages] = useState([]);

  const {rob_request_id, service_request} = useContext(LessorRequestContext);

  const fetch = useCallback(async () => {
    const messages = await messagesApi.retrieve(rob_request_id);

    setMessages(messages);
  }, [rob_request_id]);

  useEffect(() => {
    if (focused) {
      fetch();
    }
  }, [focused]);

  const scrollViewRef = useRef(null);

  const last_activity_id = Object.values(messages ?? {})
    .flatMap((item) => item)
    .filter((item) => item.activity_id)
    .slice(-1)?.[0]?.activity_id;

  return (
    <>
      <ScrollView
        style={[sy['flex-1'], sy['p-4'], sy['bg-gray95']]}
        contentContainerStyle={[sy['gap-4']]}
        ref={scrollViewRef}
        onContentSizeChange={() =>
          scrollViewRef.current?.scrollToEnd({animated: true})
        }>
        {messages &&
          Object.keys(messages).map((key) => (
            <View key={key} style={[sy['gap-2']]}>
              <Text
                style={[
                  sy.smallPlus,
                  {
                    textAlign: 'center',
                  },
                ]}>
                {moment(key, 'DD-MM-YYYY').format('D MMMM YYYY')}
              </Text>
              <View style={[sy['gap-4']]}>
                {messages[key].map((item) => {
                  const {
                    activity_id,
                    status,
                    timestamp,
                    attributes,
                    ...message
                  } = item;

                  // Create a new timestamp with date set to today, as we only need to display the time.
                  // Is it a hack? Maybe...
                  const display_timestamp = moment();
                  display_timestamp.set(
                    'hours',
                    moment(timestamp).get('hours'),
                  );
                  display_timestamp.set(
                    'minutes',
                    moment(timestamp).get('minutes'),
                  );

                  if (activity_id) {
                    return (
                      <Activity
                        key={activity_id}
                        attributes={attributes}
                        status={status}
                        display_timestamp={display_timestamp}
                        is_last_activity={activity_id === last_activity_id}
                      />
                    );
                  } else {
                    return (
                      <Message
                        key={message.message_id}
                        style={[sy['rounded-lg'], sy['p-2']]}
                        {...message}
                        company_name={service_request?.dealer.company_name}
                        timestamp={display_timestamp}
                        rob_request_id={rob_request_id}
                      />
                    );
                  }
                })}
              </View>
            </View>
          ))}
      </ScrollView>
    </>
  );
};

const LessorRequestTab = ({focused}) => {
  const {service_request, replacement_vehicle, sla, is_cancelled, refresh} =
    useContext(LessorRequestContext);

  const start_date = replacement_vehicle?.datetime;
  const start_time = replacement_vehicle?.datetime;
  const durationInDays = replacement_vehicle?.duration_in_days;
  const extensionInDays = replacement_vehicle?.extension_in_days;
  const reason = replacement_vehicle?.reason;
  const car = replacement_vehicle?.car;
  const rental_class = replacement_vehicle?.rental_class;
  const rentalCharge = replacement_vehicle?.charge_per_day;
  const kmSurcharge = replacement_vehicle?.surcharge;
  const freeKilometrage = replacement_vehicle?.free_kilometrage;
  const costs = replacement_vehicle?.costs;
  const daysCharged = replacement_vehicle?.days_charged;
  const surcharge = replacement_vehicle?.surcharge;
  const kilometrage = replacement_vehicle?.kilometrage;

  const contract_rental_class =
    service_request?.rob_contract?.leaseContract?.rentalVehicle.rentalClass;
  const total_duration_in_days = durationInDays + (extensionInDays ?? 0);

  useEffect(() => {
    if (focused) {
      refresh();
    }
  }, [focused, refresh]);

  let total_amount = 0;
  if (
    !isRobTaskCancelled(service_request?.component.rob_component.status.code)
  ) {
    if (_.isFinite(rentalCharge)) {
      total_amount += daysCharged * rentalCharge;
    }

    const surcharges = _.isFinite(kilometrage)
      ? surcharge *
        Math.max(kilometrage - freeKilometrage * total_duration_in_days, 0)
      : null;

    if (surcharges) {
      total_amount += surcharges;
    }

    if (costs) {
      total_amount += Object.keys(costs)
        .filter((key) => !key.endsWith('_comment'))
        .filter((key) => _.isFinite(costs[key]))
        .reduce((acc, key) => acc + costs[key], 0);
    }
  }

  let stage = ROB_REPLACEMENT_STAGE.reservation;
  if (
    _.isFinite(rentalCharge) &&
    _.isFinite(kmSurcharge) &&
    _.isFinite(freeKilometrage)
  ) {
    if (car?.license && _.isFinite(kilometrage)) {
      stage = ROB_REPLACEMENT_STAGE.checkin;
    } else if (car?.license) {
      stage = ROB_REPLACEMENT_STAGE.checkout;
    }
  }

  if (!service_request || !replacement_vehicle) {
    return <></>;
  }

  return (
    <>
      <ScrollView
        style={[sy['bg-gray95']]}
        contentContainerStyle={[sy['p-2'], sy['gap-2']]}>
        <Grouping
          icon={
            <DealerPersona
              dealer_id={service_request?.dealer.id}
              company_name={service_request?.dealer.company_name}
              fallback={<EventIcon fill="#4A4A49" />}
              size={24}
            />
          }
          header={
            <>
              <Text style={[sy.mediumPlus, sy.capitalize]}>
                {moment(start_date, 'YYYY-MM-DD').format('dddd')}
              </Text>
              &nbsp;{moment(start_date, 'YYYY-MM-DD').format('D MMMM')}
              &nbsp;&middot;&nbsp;{total_duration_in_days}
              &nbsp;dag
              {total_duration_in_days === 1 ? '' : 'en'}
            </>
          }
          options={{topMost: true}}>
          <GroupingRow title="Formule">
            <Text>{service_request.dealer.company_name}</Text>
          </GroupingRow>
          <GroupingRow title="Vestiging" contentStyle={[sy['flex-1']]}>
            <Text style={[sy['truncate'], sy['text-right']]}>
              {service_request.dealer.city}&nbsp;&middot;&nbsp;
              {service_request.dealer.short_name}
            </Text>
          </GroupingRow>
          <GroupingRow title="Startdatum">
            <Text>
              {moment(start_date, 'YYYY-MM-DD').format('dddd D MMMM YYYY')}
            </Text>
          </GroupingRow>
          <GroupingRow title="Starttijd">
            <Text>{moment(start_time).format('H:mm')}</Text>
          </GroupingRow>
          <GroupingRow title="Einddatum">
            <Text>
              {moment(start_date, 'YYYY-MM-DD')
                .add(total_duration_in_days - 1, 'days')
                .format('dddd D MMMM YYYY')}
            </Text>
          </GroupingRow>
          <GroupingRow title="Reden inzet">
            <Text>{REPLACEMENT_REASON_TITLE[reason]}</Text>
          </GroupingRow>
        </Grouping>
        <Grouping
          icon={
            <LessorPersona
              lessor={service_request?.lessor}
              fallback={<BusinessIcon fill={'#4A4A49'} />}
              size={24}
            />
          }
          header={service_request?.lessor}>
          <GroupingRow title="Leaseauto">
            <Text>{service_request.car.name}</Text>
          </GroupingRow>
          <GroupingRow title="Huurklasse contract">
            {contract_rental_class ? (
              <RentalClassText
                rental_class={contract_rental_class}
                rental_classes={replacement_vehicle.rob_rental_classes}
              />
            ) : (
              <Text>Niet opgegeven</Text>
            )}
          </GroupingRow>
          <GroupingRow title="Brengkosten">
            <Text>
              {service_request.rob_contract?.permissions.replacement_vehicle
                .costs.delivery ? (
                <>Toegestaan</>
              ) : (
                <>Niet toegestaan</>
              )}
            </Text>
          </GroupingRow>
          <GroupingRow title="Haalkosten">
            <Text>
              {service_request.rob_contract?.permissions.replacement_vehicle
                .costs.pickup ? (
                <>Toegestaan</>
              ) : (
                <>Niet toegestaan</>
              )}
            </Text>
          </GroupingRow>
          <GroupingRow title="Aftankkosten">
            <Text>
              {service_request.rob_contract?.permissions.replacement_vehicle
                .costs.fuel_service ? (
                <>Toegestaan</>
              ) : (
                <>Niet toegestaan</>
              )}
            </Text>
          </GroupingRow>
        </Grouping>
        {car?.license && (
          <Grouping
            icon={<CarRentalIcon fill="#4A4A49" />}
            header={
              <>
                {car.license}&nbsp;&middot;&nbsp;{car.name}
              </>
            }>
            <GroupingRow title="Kenteken">
              <Text>{car?.license}</Text>
            </GroupingRow>
            <GroupingRow title="Auto">
              <Text>{car?.name}</Text>
            </GroupingRow>
            <GroupingRow title="Huurklasse inzet">
              <Text>
                <RentalClassText
                  rental_class={car?.rental_class}
                  rental_classes={replacement_vehicle.rob_rental_classes}
                  contract_rental_class={contract_rental_class}
                />
              </Text>
            </GroupingRow>
            <GroupingRow title="Brandstofsoort">
              <FuelText fuel={car?.fuel} />
            </GroupingRow>
            <GroupingRow title="Transmissie">
              <Text>
                {car?.gearbox === 'automatic' ? 'Automaat' : 'Handgeschakeld'}
              </Text>
            </GroupingRow>
            <GroupingRow title="Carrosserie">
              <Text>{car?.is_estate ? 'Stationwagen' : 'Overig'}</Text>
            </GroupingRow>
          </Grouping>
        )}
        <Grouping
          icon={<CarTagIcon fill="#4A4A49" />}
          header={
            <>
              {rental_class}
              {stage !== ROB_REPLACEMENT_STAGE.reservation && (
                <>
                  &nbsp;&middot;&nbsp;
                  <>{Format.price(rentalCharge)}</>
                  &nbsp;(
                  <>{Format.price(kmSurcharge)}</>)
                </>
              )}
            </>
          }>
          <GroupingRow title="Huurklasse">
            <RentalClassText
              rental_class={rental_class}
              rental_classes={replacement_vehicle.rob_rental_classes}
              contract_rental_class={contract_rental_class}
              rental_car_class={car?.rental_class}
            />
          </GroupingRow>
          {stage !== ROB_REPLACEMENT_STAGE.reservation && (
            <>
              <GroupingRow title="Dagtarief">
                <Text>{Format.price(rentalCharge)}</Text>
                <GroupingRowEdit title="Dagtarief" dismissable>
                  <View style={sy['p-4']}>
                    <FormNumberInput value={rentalCharge} digits={2} />
                    <Text
                      style={[
                        sy.smallRegular,
                        {paddingLeft: 12},
                        sy['text-lightgray'],
                      ]}>
                      SLA:&nbsp;
                      {sla?.[ROB_REPLACEMENT_RENTAL_CHARGE_CODE]?.value ? (
                        <>
                          {Format.price(
                            sla?.[ROB_REPLACEMENT_RENTAL_CHARGE_CODE]?.value,
                          )}
                        </>
                      ) : (
                        <>Geen</>
                      )}
                    </Text>

                    {sla?.[ROB_REPLACEMENT_RENTAL_CHARGE_CODE]?.surcharges.map(
                      (surcharge) => (
                        <Text
                          key={surcharge.item}
                          style={[
                            sy.smallRegular,
                            {paddingLeft: 12},
                            sy['text-lightgray'],
                          ]}>
                          {surcharge.item}:&nbsp;
                          {Format.price(surcharge.amount)}
                        </Text>
                      ),
                    )}
                  </View>
                </GroupingRowEdit>
              </GroupingRow>
              <GroupingRow title="Meerkilometertarief">
                <Text>{Format.price(kmSurcharge)}</Text>
                <GroupingRowEdit
                  title="Meerkilometertarief"
                  initialValue={kmSurcharge}
                  dismissable>
                  <View style={sy['p-4']}>
                    <FormNumberInput value={kmSurcharge} digits={2} />
                    <Text
                      style={[
                        sy.smallRegular,
                        {paddingLeft: 12},
                        sy['text-lightgray'],
                      ]}>
                      SLA:&nbsp;
                      {sla?.[ROB_REPLACEMENT_KM_SURCHARGE_CODE]?.amount ? (
                        Format.price(
                          sla?.[ROB_REPLACEMENT_KM_SURCHARGE_CODE]?.amount,
                        )
                      ) : (
                        <>Geen</>
                      )}
                    </Text>
                  </View>
                </GroupingRowEdit>
              </GroupingRow>
              <GroupingRow title="Vrije kilometers per dag">
                <Text>{Format.number(freeKilometrage)}</Text>
                <GroupingRowEdit
                  title="Vrije kilometers per dag"
                  initialValue={freeKilometrage}
                  dismissable>
                  <View style={sy['p-4']}>
                    <FormNumberInput value={freeKilometrage} digits={0} />
                    <Text
                      style={[
                        sy.smallRegular,
                        {paddingLeft: 12},
                        sy['text-lightgray'],
                      ]}>
                      SLA:&nbsp;
                      {sla?.[ROB_REPLACEMENT_FREE_KM_CODE]?.amount ? (
                        <>
                          {Format.number(
                            sla?.[ROB_REPLACEMENT_FREE_KM_CODE]?.amount,
                            0,
                          )}
                          &nbsp;km&nbsp;vrij
                        </>
                      ) : (
                        <>Geen</>
                      )}
                    </Text>
                  </View>
                </GroupingRowEdit>
              </GroupingRow>
            </>
          )}
        </Grouping>

        <View style={[sy['bg-white'], sy['rounded-lg']]}>
          <View style={[sy['p-4'], sy['flex-row'], sy['gap-4']]}>
            <AssignmentIcon fill="#4A4A49" />
            <View
              style={[
                sy['flex-row'],
                sy['justify-between'],
                sy['flex-1'],
                sy['items-center'],
              ]}>
              <Text style={sy.mediumPlus}>
                Opdracht
                <>
                  &nbsp;&middot;&nbsp;
                  {isLessorStatus(service_request.status)
                    ? ROB_REPLACEMENT_STAGE_TITLE[stage]
                    : STATUS_DESCRIPTIONS[service_request.status]}
                </>
              </Text>
              <Text style={[sy.mediumPlus]}>{Format.price(total_amount)}</Text>
            </View>
          </View>
          <Divider style={sy['bg-gray88']} />
          <LineItem
            topMost={true}
            icon={<DateRangeIcon fill="#4A4A49" />}
            title="Verhuur"
            cancelled={is_cancelled}
            price={daysCharged > 0 ? daysCharged * (rentalCharge ?? 0) : <></>}
            subtitle={
              <>
                {daysCharged}&nbsp;dag{daysCharged === 1 ? '' : 'en'}
              </>
            }
          />
          {_.isFinite(kilometrage) && (
            <LineItem
              icon={<AddRoadIcon fill="#4A4A49" />}
              title="Meerkilometers"
              cancelled={is_cancelled}
              price={
                surcharge *
                Math.max(
                  kilometrage - freeKilometrage * total_duration_in_days,
                  0,
                )
              }
              subtitle={
                <>
                  {Format.number(
                    Math.max(
                      kilometrage - freeKilometrage * total_duration_in_days,
                      0,
                    ),
                  )}
                  &nbsp;km
                </>
              }
            />
          )}
          {(_.isFinite(costs.delivery) || _.isFinite(costs.pickup)) && (
            <LineItem
              icon={<SyncAltIcon fill="#4A4A49" />}
              title="Breng- en haalkosten"
              allowed={
                service_request.rob_contract?.permissions.replacement_vehicle
                  .costs.delivery ||
                service_request.rob_contract?.permissions.replacement_vehicle
                  .costs.pickup
              }
              cancelled={is_cancelled}
              price={(costs.delivery ?? 0) + (costs.pickup ?? 0)}
            />
          )}
          {(costs.fuel > 0 || costs.fuel_service > 0) && (
            <LineItem
              icon={<LocalGasStationIcon fill="#4A4A49" />}
              title="Brandstof- en aftankkosten"
              allowed={
                service_request.rob_contract?.permissions.replacement_vehicle
                  .costs.fuel ||
                service_request.rob_contract?.permissions.replacement_vehicle
                  .costs.fuel_service
              }
              cancelled={is_cancelled}
              price={(costs.fuel ?? 0) + (costs.fuel_service ?? 0)}
            />
          )}
          {costs.claim > 0 && (
            <LineItem
              icon={<GppBadIcon fill="#4A4A49" />}
              title="Eigen risico claim"
              allowed={
                service_request.rob_contract?.permissions.replacement_vehicle
                  .costs.claim
              }
              cancelled={is_cancelled}
              price={costs.claim}
            />
          )}
          {costs.misc > 0 && (
            <>
              <LineItem
                icon={<DescriptionIcon fill="#4A4A49" />}
                title="Overige kosten"
                allowed={
                  service_request.rob_contract?.permissions.replacement_vehicle
                    .costs.misc
                }
                cancelled={is_cancelled}
                price={costs.misc}
              />
              <View style={[sy['p-4'], sy['pt-0'], sy['pl-18']]}>
                <Text>{costs.misc_comment}</Text>
              </View>
            </>
          )}
        </View>
      </ScrollView>
      <Divider style={sy['bg-gray88']} />
      <View
        style={[
          sy['bg-white'],
          sy['flex-row'],
          sy['justify-between'],
          sy['items-center'],
          sy['py-3'],
          sy['px-4'],
        ]}>
        <View style={[sy['flex-row'], sy['items-center'], sy['gap-2']]}>
          <Text style={[sy.mediumBold]}>{Format.price(total_amount)}</Text>
        </View>
      </View>
    </>
  );
};

const Index = ({navigation, route}) => {
  const initialTab = route.params?.tab ?? Tabs.Request;

  const {loading, service_request, refresh} = useContext(LessorRequestContext);

  const [selectedTab, setSelectedTab] = useState(initialTab);

  const isFocused = useIsFocused();

  useLayoutEffect(() => {
    navigation.setOptions({
      headerShown: true,
      headerLeft: () => (
        <HeaderBackButton
          backImage={() => <ChevronLeftIcon />}
          onPress={() => {
            if (navigation.canGoBack()) {
              navigation.goBack();
            } else {
              navigation.replace('Tabs', {
                screen: 'Search',
              });
            }
          }}
        />
      ),
      headerStatusBarHeight: 0,
      headerTitleAlign: 'center',
      headerStyle: {
        borderBottomColor: 'transparent',
        boxShadow: 'none',
      },
      headerTitle: () =>
        loading ? null : (
          <View>
            <Text style={sy.largePlus}>
              {Format.license(service_request?.license)}
            </Text>
          </View>
        ),
      headerRight: () =>
        loading ? null : (
          <ServiceRequestStatusIcon
            style={{marginRight: 16}}
            status={service_request.status}
          />
        ),
    });
  }, [navigation, service_request, loading]);

  useEffect(() => {
    if (isFocused) {
      refresh();
    }
  }, [isFocused, refresh]);

  return (
    <View style={sy.mainView}>
      {!loading && (
        <>
          <TabBar tab={selectedTab} onChangeTab={setSelectedTab}>
            <TabBar.Tab
              label={Tabs.Request}
              icon={{
                filled: <AssignmentIcon />,
                outlined: <AssignmentOutlinedIcon />,
              }}
            />
            <TabBar.Tab
              label={Tabs.Messages}
              icon={{
                filled: <ChatIcon />,
                outlined: <ChatOutlinedIcon />,
              }}
            />
          </TabBar>
          <Divider style={sy['bg-gray88']} />
          <TabBar.Content tab={selectedTab}>
            <LessorRequestTab tab={Tabs.Request} />
            <MessagesTab tab={Tabs.Messages} />
          </TabBar.Content>
        </>
      )}
    </View>
  );
};

export default withProtectedScreen(withLessorRequestContext(Index));
